/**
 * Invitations Hooks
 *
 * TanStack Query hooks for invitation management.
 * Includes both protected (authenticated) and public (unauthenticated) operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  InvitationListItem,
  InvitationListQuery,
  InvitationPublic,
  CreateInvitation,
  AcceptInvitation,
} from '@guild/shared';

// Pagination response type
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Query keys
export const invitationKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationKeys.all, 'list'] as const,
  list: (filters: Partial<InvitationListQuery>) => [...invitationKeys.lists(), filters] as const,
  public: (token: string) => ['public-invitation', token] as const,
};

// ============================================
// PROTECTED HOOKS (require authentication)
// ============================================

/**
 * Fetch paginated invitations list
 */
export function useInvitations(options?: Partial<InvitationListQuery>) {
  const api = useApi();

  return useQuery({
    queryKey: invitationKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.status) params.set('status', options.status);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/invitations?${queryString}` : '/api/invitations';

      return api.get<PaginatedResponse<InvitationListItem>>(endpoint);
    },
  });
}

/**
 * Create new invitation
 */
export function useCreateInvitation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvitation) =>
      api.post<{ id: string; email: string; role: string; status: string; expiresAt: string }>(
        '/api/invitations',
        data
      ),
    onSuccess: () => {
      // Invalidate invitation list
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
    },
  });
}

/**
 * Revoke invitation
 */
export function useRevokeInvitation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/invitations/${id}`),
    onSuccess: () => {
      // Invalidate invitation list
      queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
    },
  });
}

// ============================================
// PUBLIC HOOKS (no authentication required)
// ============================================

/**
 * Fetch public invitation details by token
 * This endpoint does not require authentication
 */
export function usePublicInvitation(token: string | null) {
  return useQuery({
    queryKey: invitationKeys.public(token ?? ''),
    queryFn: async () => {
      if (!token) throw new Error('Token is required');

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/public/invitations/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch invitation');
      }

      return data.data as InvitationPublic;
    },
    enabled: !!token,
    retry: false, // Don't retry on failure (expired/revoked invitations)
  });
}

/**
 * Accept invitation and create account
 * This endpoint does not require authentication
 */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: async ({ token, data }: { token: string; data: AcceptInvitation }) => {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/public/invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to accept invitation');
      }

      return result.data as { message: string; email: string };
    },
  });
}
